import React, { useMemo } from 'react';

import { combineRangeAndOffset, formatUnitRatioForMemory, unixTimestampsToPrometheusRange } from '../../../utils/prometheus_utils';
import usePrometheusQuery from '../../../utils/hooks/use_prometheus_query_without_range';

import StatCard from '../../common/StatCard/StatCard';
import Icon from '../../common/icons/Icon';
import COLORS from '../../../constants/colors.constants';
import { percentFormatter } from '../../../utils/formatters';
import MemoryIcon from '../../common/icons/MemoryIcon';

const getUsedMemQuery = (rangeVector, offset) => {
  return `avg_over_time(sum(wallaroo_kube_pod_resource_usage{resource="memory"})${combineRangeAndOffset(rangeVector, offset)})`;
}

const getRequestedMemQuery = (rangeVector, offset) => {
  return `avg_over_time(sum(wallaroo_kube_pod_resource_requests{resource="memory"})${combineRangeAndOffset(rangeVector, offset)})`;
}

const MemIconComponent = () => {
  return <Icon fill={COLORS.brand} icon={MemoryIcon} size={24} useTailwindSizing />
}

export const useUsedMemQuery = ({
  startTimestamp,
  endTimestamp,
}) => {
  const query = useMemo(() => {
    const rangeObj = unixTimestampsToPrometheusRange(startTimestamp, endTimestamp);
    return getUsedMemQuery(rangeObj.rangeVector, rangeObj.offset);
  }, [endTimestamp, startTimestamp])

  const prometheusResponse = usePrometheusQuery(query);

  return {
    chartData: prometheusResponse?.data,
    isLoading: prometheusResponse?.isLoading,
    message: prometheusResponse?.error || null,
  }
}

export const useRequestedMemQuery = ({
  startTimestamp,
  endTimestamp,
}) => {
  const query = useMemo(() => {
    const rangeObj = unixTimestampsToPrometheusRange(startTimestamp, endTimestamp);
    return getRequestedMemQuery(rangeObj.rangeVector, rangeObj.offset);
  }, [endTimestamp, startTimestamp])

  const prometheusResponse = usePrometheusQuery(query);

  return {
    chartData: prometheusResponse?.data,
    isLoading: prometheusResponse?.isLoading,
    message: prometheusResponse?.error || null,
  }
}

export const ContextualAVGCPUCard = ({
  startTimestamp, endTimestamp,
}) => {
  const usedResponse = useUsedMemQuery({
    startTimestamp, endTimestamp,
  })
  const usedData = usedResponse?.chartData;
  const usedValue = usedData?.length ? usedData[1] : 0;

  const requestedResponse = useRequestedMemQuery({
    startTimestamp, endTimestamp
  })
  const requestedData = requestedResponse?.chartData;
  const requestedValue = requestedData?.length ? requestedData[1] : 0;

  const subVal = formatUnitRatioForMemory(usedValue, requestedValue);
  const subtitle = `Used ${subVal} Memory Requested`;

  const content = requestedValue ? percentFormatter.format(usedValue / requestedValue) : requestedValue;
  const isLoading = requestedResponse?.isLoading || usedResponse?.isLoading;

  return (
    <StatCard
      className=""
      icon={MemIconComponent}
      header="Average Memory Usage Over Time"
      subtitle={subtitle}
      content={`${content}`}
      isLoading={isLoading}
    />
  )
}

export default ContextualAVGCPUCard;
